import { useState, useEffect, useCallback } from "react";
import { X, Loader2, ChevronRight, Check, Paperclip, FileText, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RichTextEditor } from "./RichTextEditor";
import type { TeamKnowledgeBaseEntry, KnowledgeBaseAttachment } from "@/types/team";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface ConfluenceInlineEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    title: string;
    content: string;
    category?: string;
    is_pinned?: boolean;
    attachments?: KnowledgeBaseAttachment[];
  }) => Promise<boolean>;
  entry?: TeamKnowledgeBaseEntry | null;
  existingCategories: string[];
  saving: boolean;
}

export const ConfluenceInlineEditor = ({
  open,
  onClose,
  onSave,
  entry,
  existingCategories,
  saving
}: ConfluenceInlineEditorProps) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [useNewCategory, setUseNewCategory] = useState(false);
  const [attachments, setAttachments] = useState<KnowledgeBaseAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (entry) {
      setTitle(entry.title);
      setContent(entry.content);
      setCategory(entry.category || '');
      setIsPinned(entry.is_pinned);
      setNewCategory('');
      setUseNewCategory(false);
      // Load attachments from entry
      setAttachments(entry.attachments || []);
    } else {
      setTitle('');
      setContent('');
      setCategory('');
      setNewCategory('');
      setIsPinned(false);
      setUseNewCategory(false);
      setAttachments([]);
    }
  }, [entry, open]);

  const handleFileUpload = useCallback(async (file: File) => {
    if (!user) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error(`${file.name} ist zu groß (max. 10MB)`);
      return;
    }

    setIsUploading(true);
    try {
      const fileName = `${user.id}/${Date.now()}-${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('knowledge-base-images')
        .upload(fileName, file);

      if (uploadError) {
        toast.error(`Fehler beim Hochladen von ${file.name}`);
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('knowledge-base-images')
        .getPublicUrl(fileName);

      const newAttachment: KnowledgeBaseAttachment = {
        name: file.name,
        url: publicUrl,
        type: file.type,
        size: file.size
      };

      setAttachments(prev => [...prev, newAttachment]);
      toast.success(`${file.name} hochgeladen`);
    } finally {
      setIsUploading(false);
    }
  }, [user]);

  // Handle multiple files upload with progress
  const handleMultipleFilesUpload = useCallback(async (files: FileList | File[]) => {
    if (!user || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} ist zu groß (max. 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress({ current: 0, total: validFiles.length });
    const uploadedAttachments: KnowledgeBaseAttachment[] = [];

    try {
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        setUploadProgress({ current: i + 1, total: validFiles.length });
        
        const fileName = `${user.id}/${Date.now()}-${file.name}`;

        const { error: uploadError } = await supabase.storage
          .from('knowledge-base-images')
          .upload(fileName, file);

        if (uploadError) {
          toast.error(`Fehler beim Hochladen von ${file.name}`);
          continue;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('knowledge-base-images')
          .getPublicUrl(fileName);

        uploadedAttachments.push({
          name: file.name,
          url: publicUrl,
          type: file.type,
          size: file.size
        });
      }

      if (uploadedAttachments.length > 0) {
        setAttachments(prev => [...prev, ...uploadedAttachments]);
        toast.success(`${uploadedAttachments.length} Datei(en) hochgeladen`);
      }
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  }, [user]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleMultipleFilesUpload(files);
    }
  }, [handleMultipleFilesUpload]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('image')) return '🖼️';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('sheet')) return '📊';
    if (type.includes('powerpoint') || type.includes('presentation')) return '📽️';
    return '📎';
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmitting || saving) return;
    
    setIsSubmitting(true);
    try {
      const finalCategory = useNewCategory ? newCategory.trim() : category;
      
      const success = await onSave({
        title: title.trim(),
        content: content.trim(),
        category: finalCategory || undefined,
        is_pinned: isPinned,
        attachments: attachments
      });

      if (success) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = title.trim().length > 0 && content.trim().length > 0;
  const isButtonDisabled = !isValid || saving || isSubmitting;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[#FAFBFC] dark:bg-background">
      {/* Confluence-style Editor Header */}
      <header className="h-12 md:h-14 bg-white dark:bg-card border-b flex items-center justify-between px-3 md:px-4 sticky top-0 z-10 gap-2">
        <div className="flex items-center gap-2 md:gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground h-8 w-8 md:h-9 md:w-9"
          >
            <X className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
          
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1 text-xs md:text-sm">
            <span className="text-muted-foreground hidden sm:inline">Wissensbank</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground hidden sm:block" />
            <span className="text-foreground font-medium">
              {entry ? 'Bearbeiten' : 'Neue Seite'}
            </span>
          </nav>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Status */}
          <Badge variant="secondary" className="bg-muted border-0 text-xs hidden sm:flex">
            {saving ? 'Speichert...' : entry ? 'Entwurf' : 'Neu'}
          </Badge>

          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={saving}
            size="sm"
            className="hidden sm:flex"
          >
            Schließen
          </Button>
          
          <Button 
            onClick={handleSubmit} 
            disabled={isButtonDisabled}
            size="sm"
            className="bg-[#0052CC] hover:bg-[#0747A6] dark:bg-primary dark:hover:bg-primary/90 gap-1 md:gap-2 text-xs md:text-sm"
          >
            {saving ? (
              <>
                <Loader2 className="h-3 w-3 md:h-4 md:w-4 animate-spin" />
                <span className="hidden sm:inline">Speichern...</span>
              </>
            ) : (
              <>
                <Check className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Veröffentlichen</span>
                <span className="sm:hidden">Speichern</span>
              </>
            )}
          </Button>
        </div>
      </header>

      {/* Editor Content */}
      <div className="flex h-[calc(100vh-48px)] md:h-[calc(100vh-56px)]">
        {/* Main Editor Area */}
        <ScrollArea className="flex-1">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 md:py-8">
            {/* Title Input - Confluence Style */}
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titel der Seite"
              className={cn(
                "text-xl md:text-[32px] font-semibold border-none shadow-none px-0 h-auto py-2",
                "focus-visible:ring-0 placeholder:text-muted-foreground/40",
                "bg-transparent"
              )}
            />

            {/* Metadata Bar */}
            <div className="flex items-center gap-4 py-4 border-b mb-6 flex-wrap">
              {/* Category */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Kategorie:</span>
                {useNewCategory ? (
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Neue Kategorie..."
                    className="h-8 w-40 text-sm"
                  />
                ) : (
                  <Select 
                    value={category || "__none__"} 
                    onValueChange={(val) => setCategory(val === "__none__" ? "" : val)}
                  >
                    <SelectTrigger className="h-8 w-40 text-sm">
                      <SelectValue placeholder="Auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Keine</SelectItem>
                      {existingCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setUseNewCategory(!useNewCategory)}
                  className="text-xs text-[#0052CC] dark:text-primary hover:text-[#0052CC] dark:hover:text-primary"
                >
                  {useNewCategory ? 'Bestehende' : '+ Neue'}
                </Button>
              </div>

              {/* Pin Toggle */}
              <div className="flex items-center gap-2 ml-auto">
                <Switch
                  checked={isPinned}
                  onCheckedChange={setIsPinned}
                  id="pinned-toggle"
                />
                <Label 
                  htmlFor="pinned-toggle" 
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  Als Favorit markieren
                </Label>
              </div>
            </div>

            {/* Rich Text Editor */}
            <RichTextEditor
              content={content}
              onChange={setContent}
              placeholder="Beginne mit dem Schreiben... Nutze / für Schnellbefehle."
              onFileAttach={handleFileUpload}
            />

            {/* Attachments Section with Drag & Drop */}
            <div 
              className="mt-8 pt-6 border-t"
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Anhänge {attachments.length > 0 && `(${attachments.length})`}
                </h3>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleMultipleFilesUpload(e.target.files);
                        e.target.value = '';
                      }
                    }}
                    disabled={isUploading}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isUploading}
                    asChild
                  >
                    <span>
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {uploadProgress.total > 0 
                            ? `${uploadProgress.current}/${uploadProgress.total}` 
                            : 'Hochladen...'}
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Dateien hochladen
                        </>
                      )}
                    </span>
                  </Button>
                </label>
              </div>

              {/* Upload Progress Bar */}
              {isUploading && uploadProgress.total > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Lade {uploadProgress.current} von {uploadProgress.total} Dateien hoch...</span>
                    <span>{Math.round((uploadProgress.current / uploadProgress.total) * 100)}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300 ease-out"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Drag & Drop Zone */}
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg transition-all duration-200",
                  isDragging 
                    ? "border-primary bg-primary/5 scale-[1.02]" 
                    : "border-border/50",
                  attachments.length === 0 && !isDragging && "bg-muted/10"
                )}
              >
                {isDragging ? (
                  <div className="p-8 text-center">
                    <Upload className="h-12 w-12 mx-auto mb-3 text-primary animate-bounce" />
                    <p className="text-lg font-medium text-primary">Dateien hier ablegen</p>
                    <p className="text-sm text-muted-foreground mt-1">Loslassen zum Hochladen</p>
                  </div>
                ) : attachments.length > 0 ? (
                  <div className="p-2 space-y-2">
                    {attachments.map((att, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-3 rounded-lg border bg-background group"
                      >
                        <span className="text-xl">{getFileIcon(att.type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{att.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(att.size)}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-muted-foreground">
                    <Upload className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Keine Anhänge vorhanden</p>
                    <p className="text-xs mt-1">Klicke auf "Dateien hochladen" oder ziehe Dateien hierher</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default ConfluenceInlineEditor;
